import { useState, type CSSProperties, type ElementType } from "react"
import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import { ButtonRenderer } from "./button-renderer"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { resolveIcon } from "./resolve-icon"
import {
  positiveInteger,
  readResolvedArray,
  recordValue,
  resolvedText,
  safeNavigationUrl,
  safeResourceUrl,
  shorthandId,
} from "./property-value-utils"

function FailureNotice({ kind, value }: { kind: string; value: unknown }) {
  return (
    <span role="alert" className="inline-flex min-h-8 items-center rounded border border-destructive px-2 text-sm text-destructive">
      Unsupported {kind}: {String(value ?? "missing")}
    </span>
  )
}

function resolveLevel(value: unknown, fallback = 2): 1 | 2 | 3 | 4 | 5 | 6 {
  const level = positiveInteger(value, fallback, 6)
  return level as 1 | 2 | 3 | 4 | 5 | 6
}

type ResolveSymbol = ReturnType<typeof useRenderContext>["resolveSymbol"]

type MoneyParts = {
  amount: unknown
  currency: string
  formatted?: string
}

function resolvedValue(value: unknown, resolveSymbol: ResolveSymbol): unknown {
  return typeof value === "string" ? resolveSymbol(value) : value
}

type ResolvedImage = {
  alt: string
  rawSrc: string
  src?: string
}

function resolvedImage(value: unknown, resolveSymbol: ResolveSymbol): ResolvedImage {
  const resolved = resolvedValue(value, resolveSymbol)
  const record = resolved !== null && typeof resolved === "object" && !Array.isArray(resolved)
    ? resolved as Record<string, unknown>
    : undefined
  const resolvedSrc = record
    ? resolvedValue(record.src, resolveSymbol)
    : resolved
  const resolvedAlt = record
    ? resolvedValue(record.alt, resolveSymbol)
    : undefined
  const rawSrc = resolvedSrc === undefined || resolvedSrc === null
    ? ""
    : String(resolvedSrc)
  return {
    alt: resolvedAlt === undefined || resolvedAlt === null ? "" : String(resolvedAlt),
    rawSrc,
    src: rawSrc ? safeResourceUrl(rawSrc) : undefined,
  }
}

function currencyCode(value: unknown, resolveSymbol: ResolveSymbol, fallback = "USD"): string {
  const resolved = resolvedValue(value, resolveSymbol)
  const candidate = typeof resolved === "string" ? resolved.trim().toUpperCase() : ""
  return /^[A-Z]{3}$/.test(candidate) ? candidate : fallback
}

function moneyParts(value: unknown, currency: unknown, resolveSymbol: ResolveSymbol): MoneyParts {
  const resolved = resolvedValue(value, resolveSymbol)
  if (resolved !== null && typeof resolved === "object" && !Array.isArray(resolved)) {
    const record = resolved as Record<string, unknown>
    const formatted = resolvedText(
      record.formatted ?? record.display ?? record.label ?? record.text,
      resolveSymbol,
    )
    return {
      amount: resolvedValue(record.amount ?? record.value ?? record.price, resolveSymbol),
      currency: currencyCode(record.currency ?? record.currencyCode ?? currency, resolveSymbol),
      formatted: formatted || undefined,
    }
  }
  return {
    amount: resolved,
    currency: currencyCode(currency, resolveSymbol),
  }
}

function formatMoney(parts: MoneyParts): string | undefined {
  const numeric = typeof parts.amount === "number"
    ? parts.amount
    : typeof parts.amount === "string" && parts.amount.trim() !== ""
      ? Number(parts.amount)
      : Number.NaN
  if (Number.isFinite(numeric)) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: parts.currency,
        currencyDisplay: "code",
      }).format(numeric).replace(/\u00a0/g, " ")
    } catch {
      return `${parts.currency} ${numeric}`
    }
  }
  if (parts.formatted) return parts.formatted
  if (typeof parts.amount === "string" && parts.amount.trim()) return parts.amount.trim()
  return undefined
}

function dataAmount(value: unknown): string | number | undefined {
  return typeof value === "number" || typeof value === "string" ? value : undefined
}

function primitiveValues(value: unknown, seen = new Set<object>()): Array<string | number | boolean> {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return [value]
  if (value === null || value === undefined || typeof value !== "object") return []
  if (seen.has(value)) return []
  seen.add(value)
  const values = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>)
  return values.flatMap((entry) => primitiveValues(entry, seen))
}

export function CommerceButtonRenderer({ element }: RendererProps) {
  const text = element.label ?? element.text
  const positionedElement = commerceButtonPosition(element)
  return (
    <ButtonRenderer
      element={text === positionedElement.text
        ? positionedElement
        : { ...positionedElement, text }}
    />
  )
}

function commerceButtonPosition(element: CogoElement): CogoElement {
  const position = element.position && typeof element.position === "object"
    ? element.position as unknown as Record<string, unknown>
    : undefined
  if (!position || Object.keys(position).length === 0) return element

  const positionType = position.type
  const allowedTypes = new Set<CSSProperties["position"]>([
    "relative",
    "absolute",
    "fixed",
    "sticky",
  ])
  const positionStyle: CSSProperties = {}
  if (typeof positionType === "string"
    && allowedTypes.has(positionType as CSSProperties["position"])) {
    positionStyle.position = positionType as CSSProperties["position"]
  }
  for (const key of ["top", "right", "bottom", "left"] as const) {
    const value = position[key]
    if (typeof value === "string" || typeof value === "number") {
      positionStyle[key] = value
    }
  }

  const rawElement = element as CogoElement & {
    dataAttributes?: Record<string, string | number | boolean>
  }
  return {
    ...element,
    style: {
      ...(element.style ?? {}),
      ...positionStyle,
    },
    dataAttributes: {
      ...(rawElement.dataAttributes ?? {}),
      position: JSON.stringify(position),
    },
  } as CogoElement
}

export function SemanticHeadingRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const level = resolveLevel((element as unknown as { level?: unknown }).level)
  const Tag = `h${level}` as ElementType
  const text = resolvedText(element.text ?? element.label, resolveSymbol)
  return (
    <Tag className={element.className}>
      {text}
      {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
    </Tag>
  )
}

export function BrandLogoRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const rawSrc = resolvedText(element.src, resolveSymbol)
  const src = safeResourceUrl(rawSrc)
  const alt = resolvedText(element.alt, resolveSymbol, "Brand logo")
  if (!src) return <FailureNotice kind="brand logo source" value={rawSrc} />
  return <img src={src} alt={alt} className={element.className} />
}

export function LogoRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const rawSrc = resolvedText(element.src, resolveSymbol)
  const src = safeResourceUrl(rawSrc)
  const alt = resolvedText(element.alt, resolveSymbol, "Logo")
  const rawHref = resolvedText(element.href, resolveSymbol)
  const href = rawHref ? safeNavigationUrl(rawHref) : undefined
  if (!src) return <FailureNotice kind="logo source" value={rawSrc} />
  const image = <img src={src} alt={alt} className={element.className} />
  if (!rawHref) return image
  if (!href) return <FailureNotice kind="logo link" value={rawHref} />
  return <a href={href} aria-label={alt}>{image}</a>
}

export function CopyrightRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const rawYear = (element as unknown as { year?: unknown }).year
  const year = positiveInteger(rawYear, new Date().getFullYear(), 9999)
  const authored = resolvedText(element.text, resolveSymbol)
  const text = authored.includes("{year}")
    ? authored.replace(/\{year\}/g, String(year))
    : authored
      ? `${authored} ${year}`
      : `© ${year}`
  return <small className={element.className}>{text}</small>
}

export function HeroBannerRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { backgroundImage?: unknown; backgroundVideo?: unknown }
  const rawImage = resolvedText(raw.backgroundImage, resolveSymbol)
  const rawVideo = resolvedText(raw.backgroundVideo, resolveSymbol)
  const image = rawImage ? safeResourceUrl(rawImage) : undefined
  const video = rawVideo ? safeResourceUrl(rawVideo) : undefined
  if ((rawImage && !image) || (rawVideo && !video)) {
    return <FailureNotice kind="hero media source" value={rawVideo || rawImage} />
  }
  const variantClasses: Record<string, string> = {
    default: "relative overflow-hidden",
    centered: "relative flex overflow-hidden text-center items-center justify-center",
    split: "relative grid overflow-hidden md:grid-cols-2",
    fullscreen: "relative min-h-screen overflow-hidden",
  }
  return (
    <section
      className={cn(variantClasses[element.variant ?? "default"] ?? variantClasses.default, element.className)}
      style={image ? { backgroundImage: `url(${JSON.stringify(image).slice(1, -1)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
    >
      {video ? (
        <video src={video} autoPlay muted loop playsInline aria-label="Hero background video" className="absolute inset-0 size-full object-cover" />
      ) : null}
      <div className="relative z-10">
        {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
      </div>
    </section>
  )
}

type GalleryImage = { src?: unknown; alt?: unknown; thumbnail?: unknown }

export function ImageGalleryRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const shorthand = readResolvedArray(element, "images", resolveSymbol).map(recordValue) as GalleryImage[]
  const hasExplicitChildren = Boolean(element.children?.length)
  const [selected, setSelected] = useState(0)
  if (hasExplicitChildren) {
    return <div className={cn("grid gap-3", element.className)}>{element.children!.map((child) => <ElementRenderer key={child.id} element={child} />)}</div>
  }
  const images = shorthand.map((entry) => ({
    rawSrc: resolvedText(entry.src, resolveSymbol),
    src: safeResourceUrl(resolvedText(entry.src, resolveSymbol)),
    thumbnail: safeResourceUrl(resolvedText(entry.thumbnail ?? entry.src, resolveSymbol)),
    alt: resolvedText(entry.alt, resolveSymbol, "Gallery image"),
  }))
  if (images.length === 0) return <FailureNotice kind="gallery images" value="empty" />
  const active = images[Math.min(selected, images.length - 1)]
  if (!active.src) return <FailureNotice kind="gallery image source" value={active.rawSrc} />
  return (
    <figure className={cn("grid gap-3", element.className)}>
      <img src={active.src} alt={active.alt} className="w-full object-cover" />
      <div className="flex gap-2" role="list" aria-label="Image thumbnails">
        {images.map((image, index) => image.thumbnail ? (
          <button key={`${image.thumbnail}-${index}`} type="button" role="listitem" aria-label={`${image.alt} thumbnail`} aria-current={selected === index ? "true" : undefined} onClick={() => setSelected(index)}>
            <img src={image.thumbnail} alt={`${image.alt} thumbnail`} className="size-16 object-cover" />
          </button>
        ) : <FailureNotice key={index} kind="gallery thumbnail source" value={image.rawSrc} />)}
      </div>
    </figure>
  )
}

export function LinkColumnsRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  if (element.children?.length) {
    return <div className={cn("grid gap-6 sm:grid-cols-2 lg:grid-cols-4", element.className)}>{element.children.map((child) => <ElementRenderer key={child.id} element={child} />)}</div>
  }
  const columns = readResolvedArray(element, "columns", resolveSymbol).map(recordValue)
  return (
    <div className={cn("grid gap-6 sm:grid-cols-2 lg:grid-cols-4", element.className)}>
      {columns.map((column, columnIndex) => {
        const links = Array.isArray(column.links) ? column.links.map(recordValue) : []
        return (
          <section key={String(column.id ?? columnIndex)}>
            {column.title || column.label ? <h2 className="font-semibold">{resolvedText(column.title ?? column.label, resolveSymbol)}</h2> : null}
            <ul>{links.map((link, linkIndex) => {
              const label = resolvedText(link.label ?? link.text, resolveSymbol)
              const rawHref = resolvedText(link.href, resolveSymbol)
              const href = safeNavigationUrl(rawHref)
              return <li key={String(link.id ?? linkIndex)}>{href ? <a href={href}>{label}</a> : <FailureNotice kind="link" value={rawHref} />}</li>
            })}</ul>
          </section>
        )
      })}
    </div>
  )
}

export function MenuListRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const explicit = element.children ?? []
  const items = explicit.length
    ? explicit
    : readResolvedArray(element, "items", resolveSymbol).map((raw, index) => {
        const item = recordValue(raw)
        return {
          ...item,
          id: String(item.id ?? shorthandId(element.id, "items", index)),
          type: "menu-item",
          label: item.label ?? item.text,
        } as unknown as CogoElement
      })
  return <nav aria-label={element.ariaLabel ?? "Menu"} className={element.className}><ul className="flex flex-wrap gap-4">{items.map((item) => <li key={item.id}><ElementRenderer element={item} /></li>)}</ul></nav>
}

export function MenuItemRenderer({ element }: RendererProps) {
  const { resolveSymbol, navigate } = useRenderContext()
  const raw = element as unknown as { active?: boolean | string }
  const label = resolvedText(element.label ?? element.text, resolveSymbol)
  const rawHref = resolvedText(element.href, resolveSymbol, "#")
  const href = safeNavigationUrl(rawHref)
  const activeResolved = typeof raw.active === "string" ? resolveSymbol(raw.active) : raw.active
  const active = activeResolved === true || activeResolved === "true"
  if (!href) return <FailureNotice kind="menu link" value={rawHref} />
  return (
    <a
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(active ? "font-semibold text-foreground underline underline-offset-4" : "text-muted-foreground", element.className)}
      onClick={(event) => {
        if (navigate && href.startsWith("/") && !href.startsWith("//")) {
          event.preventDefault()
          navigate(href)
        }
      }}
    >
      {label}
    </a>
  )
}

export function SocialIconsRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  if (element.children?.length) return <div className={cn("flex gap-3", element.className)}>{element.children.map((child) => <ElementRenderer key={child.id} element={child} />)}</div>
  const icons = readResolvedArray(element, "icons", resolveSymbol).map(recordValue)
  return (
    <div className={cn("flex gap-3", element.className)}>
      {icons.map((item, index) => {
        const name = resolvedText(item.icon ?? item.name, resolveSymbol)
        const Icon = resolveIcon(name)
        const label = resolvedText(item.label, resolveSymbol, name || `Social link ${index + 1}`)
        const rawHref = resolvedText(item.href, resolveSymbol)
        const href = safeNavigationUrl(rawHref)
        if (!Icon || !href) return <FailureNotice key={index} kind="social icon" value={name || rawHref} />
        return <a key={String(item.id ?? index)} href={href} aria-label={label}><Icon className="size-5" /></a>
      })}
    </div>
  )
}

export function ConfirmationDisplayRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { message?: unknown; orderId?: unknown }
  const message = resolvedText(raw.message ?? element.text, resolveSymbol, "Order confirmed")
  const orderId = resolvedText(raw.orderId, resolveSymbol)
  return (
    <section role="status" className={cn("rounded-lg border p-6", element.className)}>
      <p className="font-semibold">{message}</p>
      {orderId ? <p>Order <strong>{orderId}</strong></p> : null}
      {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
    </section>
  )
}

export function ProductCardRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { image?: unknown; price?: unknown }
  const image = resolvedImage(raw.image, resolveSymbol)
  const title = resolvedText(element.title ?? element.text, resolveSymbol)
  const priceParts = moneyParts(raw.price, undefined, resolveSymbol)
  const price = formatMoney(priceParts)
  const hasPrice = raw.price !== undefined && raw.price !== null
  const priceValue = hasPrice
    ? JSON.stringify({ amount: priceParts.amount, currency: priceParts.currency })
    : undefined
  return (
    <article
      className={cn("overflow-hidden rounded-lg border", element.className)}
      data-price={hasPrice ? dataAmount(priceParts.amount) : undefined}
      data-price-amount={hasPrice ? dataAmount(priceParts.amount) : undefined}
      data-price-currency={hasPrice ? priceParts.currency : undefined}
      data-value={priceValue}
    >
      {image.rawSrc ? image.src ? <img src={image.src} alt={image.alt || title || "Product"} className="aspect-square w-full object-cover" /> : <FailureNotice kind="product image" value={image.rawSrc} /> : null}
      <div className="p-4">
        {title ? <h2 className="font-semibold">{title}</h2> : null}
        {hasPrice ? <p data-price={priceParts.currency}>{price ?? "Price unavailable"}</p> : null}
        {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
      </div>
    </article>
  )
}

export function PriceDisplayRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { amount?: unknown; currency?: unknown }
  const hasAmount = raw.amount !== undefined && raw.amount !== null
  const hasCurrency = raw.currency !== undefined && raw.currency !== null
  const parts = moneyParts(raw.amount, raw.currency, resolveSymbol)
  const formatted = hasAmount
    ? formatMoney(parts) ?? "Price unavailable"
    : hasCurrency
      ? parts.currency
      : undefined
  return (
    <span
      className={cn("inline-flex items-baseline gap-2 tabular-nums", element.className)}
      data-amount={hasAmount ? dataAmount(parts.amount) : undefined}
      data-currency={hasAmount || hasCurrency ? parts.currency : undefined}
    >
      {formatted ? <span>{formatted}</span> : null}
      {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
    </span>
  )
}

export function CartLinesRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const authoredLines = readResolvedArray(element, "lines", resolveSymbol).map(recordValue)
  const hasExplicitChildren = Boolean(element.children?.length)
  const lines = hasExplicitChildren
    ? element.children ?? []
    : authoredLines.map((line, index) => {
        const quantity = Number(line.quantity)
        const unitPrice = Number(line.unitPrice)
        const computedSubtotal = Number.isFinite(quantity) && Number.isFinite(unitPrice)
          ? quantity * unitPrice
          : undefined
        return {
          ...line,
          id: String(line.id ?? shorthandId(element.id, "lines", index)),
          type: "cart-line",
          title: line.title ?? line.name ?? line.label,
          kind: line.kind ?? "product",
          subTotal: line.subTotal ?? line.subtotal ?? computedSubtotal,
        } as unknown as CogoElement
      })
  return (
    <section
      role="list"
      aria-label={resolvedText(element.ariaLabel, resolveSymbol, "Cart items")}
      className={cn("grid gap-3", element.className)}
      data-line-count={lines.length}
    >
      {lines.map((line, index) => (
        <div key={line.id} role="listitem">
          {!hasExplicitChildren ? primitiveValues(authoredLines[index]).map((value, valueIndex) => (
            <span
              key={`${line.id}-semantic-${valueIndex}`}
              aria-hidden="true"
              className="sr-only"
              data-lines={String(value)}
            />
          )) : null}
          <ElementRenderer element={line} />
        </div>
      ))}
    </section>
  )
}

type TotalsSummaryValues = {
  subtotal?: unknown
  tax?: unknown
  shipping?: unknown
  total?: unknown
  currency?: unknown
}

export function TotalsSummaryRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as TotalsSummaryValues
  const currency = currencyCode(raw.currency, resolveSymbol)
  const rows = [
    ["Subtotal", raw.subtotal, false],
    ["Tax", raw.tax, false],
    ["Shipping", raw.shipping, false],
    ["Total", raw.total, true],
  ] as const
  const authoredRows = rows.filter(([, value]) => value !== undefined && value !== null)
  const hasCurrency = raw.currency !== undefined && raw.currency !== null
  const singleAmount = authoredRows.length === 1
    ? dataAmount(resolvedValue(authoredRows[0][1], resolveSymbol))
    : undefined
  return (
    <section
      aria-label={resolvedText(element.ariaLabel, resolveSymbol, "Order totals")}
      className={cn("grid gap-2", element.className)}
      data-currency={hasCurrency || authoredRows.length > 0 ? currency : undefined}
      data-amount={singleAmount}
      data-shipping={dataAmount(resolvedValue(raw.shipping, resolveSymbol))}
      data-subtotal={dataAmount(resolvedValue(raw.subtotal, resolveSymbol))}
      data-tax={dataAmount(resolvedValue(raw.tax, resolveSymbol))}
      data-total={dataAmount(resolvedValue(raw.total, resolveSymbol))}
      data-value={singleAmount}
    >
      {authoredRows.map(([label, value, emphasized]) => {
        const parts = moneyParts(value, currency, resolveSymbol)
        const formatted = formatMoney(parts) ?? "Amount unavailable"
        return (
          <div
            key={label}
            className={cn("flex items-baseline justify-between gap-4", emphasized && "border-t pt-2 font-semibold")}
          >
            <span>{label}</span>
            <span data-amount={dataAmount(parts.amount)} data-currency={parts.currency}>{formatted}</span>
          </div>
        )
      })}
      {authoredRows.length === 0 && hasCurrency ? (
        <p className="text-sm text-muted-foreground">Currency <span className="font-medium text-foreground">{currency}</span></p>
      ) : null}
      {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
    </section>
  )
}

export function CartLineRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { image?: unknown; kind?: unknown; unitPrice?: unknown; quantity?: unknown; subTotal?: unknown }
  const image = resolvedImage(raw.image, resolveSymbol)
  const kind = resolvedText(raw.kind, resolveSymbol, "product")
  const title = resolvedText(element.title ?? element.text, resolveSymbol)
  const unitPrice = resolvedText(raw.unitPrice, resolveSymbol)
  const quantity = positiveInteger(raw.quantity, 1, 9999)
  const subtotal = resolvedText(raw.subTotal, resolveSymbol)
  const kindClass = kind === "discount" ? "text-green-700" : kind === "tax" || kind === "shipping" ? "text-muted-foreground" : "text-foreground"
  return (
    <article
      className={cn("grid grid-cols-[auto_1fr_auto] items-center gap-3", kindClass, element.className)}
      data-kind={kind}
      data-quantity={quantity}
      data-sub-total={subtotal || undefined}
      data-unit-price={unitPrice || undefined}
    >
      {image.rawSrc ? image.src ? <img src={image.src} alt={image.alt || (title ? `${title} image` : "Cart item")} className="size-16 object-cover" /> : <FailureNotice kind="cart image" value={image.rawSrc} /> : null}
      <div><h3>{title || kind}</h3><p>{quantity} × {unitPrice}</p></div>
      <strong>{subtotal || unitPrice}</strong>
      {element.children?.map((child) => <ElementRenderer key={child.id} element={child} />)}
    </article>
  )
}

const CHECKOUT_STEPS = ["cart", "address", "fulfillment", "payment", "review", "confirmation"]

export function StepIndicatorRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const rawCurrent = (element as unknown as { currentStep?: unknown }).currentStep
  const resolvedCurrent = resolvedText(rawCurrent, resolveSymbol, "cart")
  const numericCurrent = Number(resolvedCurrent)
  // JITRULE Task 2: step children may nest their fields under `props`.
  const steps = (element.children ?? []).map(liftV4Props)
  const authoredCurrentIndex = steps.findIndex((child) => {
    const step = resolvedText((child as unknown as { step?: unknown }).step, resolveSymbol)
    return step === resolvedCurrent || child.id === resolvedCurrent
  })
  const currentIndex = Number.isFinite(numericCurrent)
    ? Math.max(0, Math.floor(numericCurrent))
    : authoredCurrentIndex >= 0
      ? authoredCurrentIndex
      : Math.max(0, CHECKOUT_STEPS.indexOf(resolvedCurrent))
  return (
    <ol
      className={cn("flex items-center gap-2", element.className)}
      aria-label="Checkout progress"
      data-current-step={resolvedCurrent}
      data-current-step-index={currentIndex}
    >
      {steps.map((child, index) => {
        const raw = child as unknown as { step?: unknown; completed?: unknown; active?: unknown }
        const step = resolvedText(raw.step, resolveSymbol, CHECKOUT_STEPS[index] ?? String(index + 1))
        const completedValue = typeof raw.completed === "string" ? resolveSymbol(raw.completed) : raw.completed
        const activeValue = typeof raw.active === "string" ? resolveSymbol(raw.active) : raw.active
        const completed = completedValue === true || completedValue === "true" || index < currentIndex
        const active = activeValue === true || activeValue === "true" || index === currentIndex || step === resolvedCurrent
        const label = resolvedText(child.label ?? child.text, resolveSymbol, step)
        return (
          <li key={child.id} aria-current={active ? "step" : undefined} className={cn("flex items-center gap-2", completed ? "text-primary" : active ? "font-semibold text-foreground" : "text-muted-foreground", child.className)}>
            <span aria-hidden="true" className="flex size-7 items-center justify-center rounded-full border">{completed ? "✓" : index + 1}</span>
            <span>{label}</span>
          </li>
        )
      })}
    </ol>
  )
}

export function CheckoutStepRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const raw = element as unknown as { step?: unknown; completed?: unknown; active?: unknown }
  const step = resolvedText(raw.step, resolveSymbol, element.id)
  const label = resolvedText(element.label ?? element.text, resolveSymbol, step)
  const completedRaw = typeof raw.completed === "string" ? resolveSymbol(raw.completed) : raw.completed
  const activeRaw = typeof raw.active === "string" ? resolveSymbol(raw.active) : raw.active
  const completed = completedRaw === true || completedRaw === "true"
  const active = activeRaw === true || activeRaw === "true"
  return (
    <div
      role="listitem"
      aria-current={active ? "step" : undefined}
      className={cn(completed ? "text-primary" : active ? "font-semibold" : "text-muted-foreground", element.className)}
      data-active={active}
      data-completed={completed}
      data-step={step}
    >
      {completed ? "✓ " : ""}{label}
    </div>
  )
}
